import { Duration, ZonedDateTime, ZoneId } from '@js-joda/core';
import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import get from 'lodash/get';
import React from 'react';
import { useRecordContext } from 'react-admin';

import { dateAndTime } from '../../../utils/date';

export type ZoneColumn = { header: string; zone: ZoneId };

const formatInZone = (zdt: ZonedDateTime, zone: ZoneId): string =>
  dateAndTime(zdt.withZoneSameInstant(zone));

const formatLocal = (zdt: ZonedDateTime): string =>
  formatInZone(zdt, ZoneId.systemDefault());

/** Parses an IANA zone id (e.g. "America/New_York"), returning null if invalid. */
export const parseZone = (zone: string): ZoneId | null => {
  try {
    return ZoneId.of(zone);
  } catch {
    return null;
  }
};

const SECONDS_PER: ReadonlyArray<readonly [string, number]> = [
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

/**
 * Humanizes a number of seconds as its two most-significant units, e.g.
 * 2_649_827 -> "30 days 16 hours". Always non-negative; callers add "ago"/"in".
 */
const formatDuration = (seconds: number): string => {
  let remaining = Math.abs(Math.trunc(seconds));
  const parts: string[] = [];
  for (const [unit, size] of SECONDS_PER) {
    const value = Math.floor(remaining / size);
    remaining -= value * size;
    if (value > 0) {
      parts.push(`${value} ${value === 1 ? unit : `${unit}s`}`);
    }
  }
  return parts.length > 0 ? parts.slice(0, 2).join(' ') : 'less than a second';
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

type StatusColor = 'warning' | 'success' | 'error';

type Status = {
  label: string;
  detail: string;
  color: StatusColor;
};

const getStatus = (
  start: ZonedDateTime | null,
  end: ZonedDateTime | null,
  now: ZonedDateTime,
): Status => {
  if (start && start.isAfter(now)) {
    return {
      label: 'Pending',
      detail: `starts in ${formatDuration(Duration.between(now, start).seconds())}`,
      color: 'warning',
    };
  }
  if (end && !end.isAfter(now)) {
    return {
      label: 'Expired',
      detail: `ended ${formatDuration(Duration.between(end, now).seconds())} ago`,
      color: 'error',
    };
  }
  return {
    label: 'Active',
    detail: end
      ? `expires in ${formatDuration(Duration.between(now, end).seconds())}`
      : 'no end date',
    color: 'success',
  };
};

/**
 * A progress bar from start to end with a "now" marker, labelled with the start,
 * end, and current dates (local timezone). Rendered only for a positive-length
 * window, so start/end are guaranteed present and end is after start.
 */
const WindowTimeline: React.FC<{
  start: ZonedDateTime;
  end: ZonedDateTime;
  now: ZonedDateTime;
  color: StatusColor;
}> = ({ start, end, now, color }) => {
  const startSec = start.toEpochSecond();
  const windowSeconds = end.toEpochSecond() - startSec;
  const elapsedSeconds = now.toEpochSecond() - startSec;
  const percentElapsed = clamp((elapsedSeconds / windowSeconds) * 100, 0, 100);

  // Keep the "now" label inside the bar's bounds at the extremes.
  const nowLabelAlign =
    percentElapsed <= 10
      ? 'translateX(0)'
      : percentElapsed >= 90
        ? 'translateX(-100%)'
        : 'translateX(-50%)';

  return (
    <Box sx={{ my: 2 }}>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'relative', height: 18 }}>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              left: `${percentElapsed}%`,
              bottom: 2,
              transform: nowLabelAlign,
              whiteSpace: 'nowrap',
              fontWeight: 600,
            }}
          >
            Now · {formatLocal(now)}
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: 3,
            zIndex: 1,
            left: `${percentElapsed}%`,
            transform: 'translateX(-50%)',
            width: '1px',
            height: '1px',
            borderStyle: 'solid',
            borderWidth: 5,
            borderTopColor: 'text.primary',
            borderBottomColor: 'transparent',
            borderLeftColor: 'transparent',
            borderRightColor: 'transparent',
          }}
        />
        <LinearProgress
          variant="determinate"
          value={percentElapsed}
          color={color}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatLocal(start)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatLocal(end)}
        </Typography>
      </Box>
    </Box>
  );
};

interface EligibilityWindowViewProps {
  start: ZonedDateTime | null;
  end: ZonedDateTime | null;
  /** Optional extra timezone column (e.g. the member's zone). */
  userZone?: ZoneColumn | null;
}

/**
 * Presentational eligibility window: a derived status chip, a timeline bar, and
 * a compact Local/UTC table, given already-resolved start/end instants. Source-
 * agnostic, so it works from a saved record or live form state.
 */
export const EligibilityWindowView: React.FC<EligibilityWindowViewProps> = ({
  start,
  end,
  userZone,
}) => {
  if (!start && !end) {
    return (
      <Box sx={{ color: 'text.secondary' }}>No eligibility window set</Box>
    );
  }

  const now = ZonedDateTime.now();
  const status = getStatus(start, end, now);
  const duration =
    start && end
      ? formatDuration(Duration.between(start, end).seconds())
      : null;

  const columns: ZoneColumn[] = [
    { header: 'Local (your timezone)', zone: ZoneId.systemDefault() },
    ...(userZone ? [userZone] : []),
    { header: 'UTC', zone: ZoneId.UTC },
  ];
  const rows: ReadonlyArray<readonly [string, ZonedDateTime | null]> = [
    ['Start', start],
    ['End', end],
  ];

  return (
    <Box sx={{ maxWidth: 750 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
        <Chip size="small" color={status.color} label={status.label} />
        <Box sx={{ color: 'text.secondary' }}>{status.detail}</Box>
      </Box>
      {start && end && end.isAfter(start) && (
        <WindowTimeline
          start={start}
          end={end}
          now={now}
          color={status.color}
        />
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            {columns.map((col) => (
              <TableCell key={col.header}>{col.header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([label, zdt]) => (
            <TableRow key={label}>
              <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.header}>
                  {zdt ? formatInZone(zdt, col.zone) : '—'}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {duration && (
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
              <TableCell colSpan={columns.length}>{duration}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

interface EligibilityWindowProps {
  startSource: string;
  endSource: string;
  /**
   * Optional record path to the subject's IANA timezone (e.g. "timezone" or
   * "user.timezone"). When present and valid, adds a column showing the dates in
   * that zone.
   */
  timezoneSource?: string;
}

const parseRecordZdt = (value: unknown): ZonedDateTime | null =>
  typeof value === 'string' ? ZonedDateTime.parse(value) : null;

/**
 * Record-bound EligibilityWindow: reads start/end (and an optional timezone)
 * from the current record and renders {@link EligibilityWindowView}.
 */
export const EligibilityWindow: React.FC<EligibilityWindowProps> = ({
  startSource,
  endSource,
  timezoneSource,
}) => {
  const record = useRecordContext();
  const start = record ? parseRecordZdt(get(record, startSource)) : null;
  const end = record ? parseRecordZdt(get(record, endSource)) : null;

  const userZoneRaw =
    timezoneSource && record ? get(record, timezoneSource) : null;
  const zone = userZoneRaw ? parseZone(userZoneRaw) : null;
  const userZone: ZoneColumn | null = zone
    ? { header: `User (${userZoneRaw})`, zone }
    : null;

  return <EligibilityWindowView start={start} end={end} userZone={userZone} />;
};
